import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Link as MLink,
  Card,
  List,
  ListItem,
  CardContent,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AddCircleTwoTone,
  AddTwoTone,
  DeleteTwoTone,
  HomeTwoTone,
} from '@mui/icons-material';
import { AddEditGuidanceTourHealthDisabilityDialog } from '../components/AddEditGuidanceTourHealthDisabilityDialog';
import { IGuideTourCustomerDisability } from '../utils/types';
import {
  deleteGuideTourHealthDisability,
  guideTourHealthCustomerDisability,
} from '../api';
import { AuthContext } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import PageHeader from '../components/PageHeader';

export function GuidanceTourHealthDisabilityPage() {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { salesIndividualId } = useParams<{ salesIndividualId: string }>();
  const [searchParams] = useSearchParams();
  const tourId = searchParams.get('tourId');
  const [customerDisability, setCustomerDisability] = useState<
    IGuideTourCustomerDisability[]
  >([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const showSnackbar = useAlertSnackbar();

  useEffect(() => {
    fetchGuideTourHealthCustomerDisabilities(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleAdd = async () => {
    setOpenDialog(true);
  };

  const handleSaveCustomerDisability = async () => {
    setOpenDialog(false);
    await fetchGuideTourHealthCustomerDisabilities(salesIndividualId);
  };

  const handleDelete = async (id?: number, customerId?: number) => {
    await deleteGuideTourHealthCustomerDisability(
      id,
      salesIndividualId,
      customerId,
    );
    await fetchGuideTourHealthCustomerDisabilities(salesIndividualId);
  };

  const deleteGuideTourHealthCustomerDisability = async (
    id?: number,
    guideSalesIndividualId?: string,
    customerId?: number,
  ) => {
    try {
      const res = await deleteGuideTourHealthDisability(
        id,
        token,
        guideSalesIndividualId,
        customerId,
      );

      if (!res.ok) {
        let errorMessage = `Request failed with status ${res.status}`;
        try {
          const errorResponse = await res.json();
          errorMessage = errorResponse.detail || errorMessage;
        } catch (jsonError) {
          console.error('Error parsing JSON response:', jsonError);
        }

        throw new Error(errorMessage);
      }

      return res;
    } catch (e: any) {
      const errorMessage = e.message || 'Beklenmeyen bir hata oluştu.';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      console.error('Delete Guide Tour Health Customer Disability Error:', e);
    }
  };

  const fetchGuideTourHealthCustomerDisabilities = async (
    guideSalesIndividualId?: string,
  ) => {
    setLoading(true);
    try {
      const res = await guideTourHealthCustomerDisability(
        token,
        guideSalesIndividualId,
      );
      const data = await res.json();
      if (res?.status === 200) {
        setCustomerDisability(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (!salesIndividualId) return null;

  const groupedDisabilities = customerDisability.reduce<
    Record<string, IGuideTourCustomerDisability[]>
  >((acc, item) => {
    const key = item.salesIndividualId;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ maxWidth: 'lg' }}>
      <PageHeader
        title={t('guidanceTourHealth.disability')}
        actions={
          <Button
            onClick={handleAdd}
            size="small"
            color="secondary"
            variant="contained"
            startIcon={<AddCircleTwoTone />}
          >
            {t('guidanceTourHealth.add')}
          </Button>
        }
      />

      <Breadcrumbs sx={{ mb: 1 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink to="/account/guidance" component={Link}>
          <Typography>{t('nav.guidance')}</Typography>
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}?tourId=${tourId}`}
        >
          {t('nav.tourId', { tourId })}
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}/health?tourId=${tourId}`}
        >
          {t('guidanceTourHealth.title', { tourId })}
        </MLink>
        <Typography>{t('guidanceTourHealth.disability')}</Typography>
      </Breadcrumbs>

      {loading ? (
        <LoadingSpinner loadingText={t('balancesQueryPage.loading')} />
      ) : (
        Object.keys(groupedDisabilities).map((id) => {
          const groupItems = groupedDisabilities[id];
          const customerName = `${groupItems[0].customerFirstName} ${groupItems[0].customerLastName}`;
          return (
            <Box key={id} sx={{ mb: 2 }}>
              <Card>
                <Typography variant="h6" sx={{ mt: 2, ml: 2 }}>
                  {customerName}
                </Typography>
                <List>
                  {groupItems.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        mt: index !== 0 ? -4 : 0,
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {item.disability}
                        </Typography>
                      </CardContent>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleDelete(
                              item.salesIndividualDisabilityId,
                              item.salesIndividualId,
                            )
                          }
                          aria-label="delete"
                        >
                          <DeleteTwoTone />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Box>
          );
        })
      )}

      <AddEditGuidanceTourHealthDisabilityDialog
        open={openDialog}
        onClose={handleDialogClose}
        onSave={handleSaveCustomerDisability}
        guideSalesIndividualId={salesIndividualId}
      />
    </Box>
  );
}
